import { MutableRefObject } from 'react';

import { cloneDeep } from 'lodash';

type RolesActionData<P extends string, R extends string> = {
  permission?: P;
  role: R;
  value: boolean;
};

export type RolesAction<P extends string, R extends string> = {
  command: 'SET' | 'RESET';
  data: RolesActionData<P, R> | Record<R, RolesState<P>>;
};

type EditPermissionErrors = {
  permissions?: boolean;
};

type EditPermissionErrorsAction = {
  field: keyof EditPermissionErrors | 'SET';
  error?: boolean;
  newState?: EditPermissionErrors;
};

export type EditPermissionErrorsReducer = (
  state: EditPermissionErrors,
  action: EditPermissionErrorsAction,
) => EditPermissionErrors;

export type RolesReducer<P extends string, R extends string> = (
  state: Record<R, RolesState<P>>,
  action: RolesAction<P, R>,
) => Record<R, RolesState<P>>;

const rolesReducer = <P extends string, R extends string>(
  state: Record<R, RolesState<P>> | undefined,
  action: RolesAction<P, R>,
): Record<R, RolesState<P>> => {
  if (!state) {
    return {} as Record<R, RolesState<P>>;
  }
  const { command, data } = action;
  switch (command) {
    case 'SET': {
      const { permission, role, value } = data as RolesActionData<P, R>;

      if (permission) {
        const permissionValues = Object.entries(state[role].permissions)
          .filter(
            (permissionEntry: [P, PermissionsState]) =>
              !permissionEntry[1]?.isHidden,
          )
          .map(([permissionValue, { isChecked }]: [P, PermissionsState]) =>
            permissionValue === permission ? value : isChecked,
          );
        const areAllPermissionsChecked = permissionValues.length
          ? permissionValues.reduce(
              (acc: boolean, isChecked: boolean) => acc && isChecked,
              true,
            )
          : false;

        return {
          ...state,
          [role]: {
            ...state[role],
            isChecked: areAllPermissionsChecked,
            permissions: {
              ...state[role].permissions,
              [permission]: {
                ...state[role].permissions[permission],
                isChecked: value,
              },
            },
          },
        };
      }

      const permissions: Record<P, PermissionsState> = cloneDeep(
        state[role].permissions,
      );
      Object.keys(permissions).forEach((perm: P) => {
        if (!permissions[perm].isHidden) {
          Object.assign(permissions[perm], { isChecked: value });
        }
      });

      return {
        ...state,
        [role]: {
          ...state[role],
          isChecked: value,
          permissions,
        },
      };
    }
    case 'RESET':
      return data as Record<R, RolesState<P>>;
    default:
      return state;
  }
};

const errorsReducer =
  (dispatching: MutableRefObject<boolean>): EditPermissionErrorsReducer =>
  (state, action) => {
    const { field, error: fieldError = false, newState = {} } = action;
    let updatedState: EditPermissionErrors = newState;

    if (field !== 'SET') {
      updatedState = {
        ...state,
        [field]: fieldError,
      };
    }

    Object.assign(dispatching, { current: false });
    return updatedState;
  };

export { rolesReducer, errorsReducer };
